import { useCallback, useState } from "react";
import Swal from "sweetalert2";
import { useSnackbar } from "src/context/ShowMessage";
import { ApiResponse } from "src/apis";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";

const Methods = {
  cancel: (moduleName: string) =>
    i18n.t("common.messages.cancelMessage", {
      module: moduleName,
    }),
  void: (moduleName: string) =>
    i18n.t("common.messages.voidMessage", {
      module: moduleName,
    }),
  complete: (moduleName: string) =>
    i18n.t("common.messages.completeMessage", {
      module: moduleName,
    }),
  noShow: (moduleName: string) =>
    i18n.t("common.messages.noShowMessage", {
      module: moduleName,
    }),
};

type MethodAction = keyof typeof Methods;

const useDataDeletionRestoration = () => {
  const { t } = useTranslation();
  const [isRestoring, setIsRestoring] = useState(false);
  const { displayMessage } = useSnackbar();

  const handleDeleteRestoreData = useCallback(
    async (
      id: number,
      restoreData?: Function,
      callBack?: Function,
      isRestore: boolean = true,
      module: string = "user",
      action?: MethodAction,
      cancelCallBack?: Function,
      customDeleteMessage?: string // 👈 New optional parameter for custom delete message
    ) => {
      if (!id || !restoreData) return;
      const moduleName = t(`common.apiMsgs.moduleNames.${module}`);

      // Determine the confirmation message
      let confirmationMessage = "";
      if (!!action) {
        confirmationMessage = Methods[action](moduleName);
      } else if (isRestore) {
        confirmationMessage = t("common.messages.restoreMessage", { module: moduleName });
      } else {
        // Use custom message if provided, otherwise default delete message
        confirmationMessage = customDeleteMessage
          ? customDeleteMessage.replace("{{module}}", moduleName)
          : t("common.messages.deleteMessage", { module: moduleName });
      }

      Swal.fire({
        title: t("common.title.confirm"),
        text: confirmationMessage, // 👈 Use the computed message
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        didOpen: () => {
          const MAX_Z_INDEX = "3001";
          const BACKDROP_Z_INDEX = "3000";

          const setElementZIndex = (selector: string, value: string): void => {
            const element = document.querySelector<HTMLElement>(selector);
            element && (element.style.zIndex = value);
          };

          const setElementsZIndex = (selectors: string[], value: string): void => {
            selectors.forEach((selector) => {
              document
                .querySelectorAll<HTMLElement>(selector)
                .forEach((el) => (el.style.zIndex = value));
            });
          };

          setElementsZIndex([".swal2-container", ".swal2-popup"], MAX_Z_INDEX);
          setElementZIndex(".swal2-backdrop", BACKDROP_Z_INDEX);
        },
        confirmButtonText: !!action
          ? t("common.buttons.ok")
          : isRestore
            ? t("common.buttons.restore")
            : t("common.buttons.delete"),
        cancelButtonText: t("common.buttons.cancel"),
      }).then(async (result: any) => {
        if (result.isConfirmed) {
          setIsRestoring(true);
          try {
            const response: ApiResponse = await restoreData(id);
            setIsRestoring(false);
            if (response.status === 200) {
              callBack && callBack(response.data);
              displayMessage(
                !!action
                  ? t(`common.apiMsgs.${action}Success`, { module: moduleName })
                  : isRestore
                    ? t("common.apiMsgs.restoreSuccess", { module: moduleName })
                    : t("common.apiMsgs.deleteSuccess", { module: moduleName }),
                "success"
              );
            } else {
              displayMessage(
                !!action
                  ? t(`common.apiMsgs.${action}Failed`, { module: moduleName })
                  : isRestore
                    ? t("common.apiMsgs.restoreFailed", { module: moduleName })
                    : t("common.apiMsgs.deleteFailed", { module: moduleName }),
                "error"
              );
            }
          } catch (e) {
            setIsRestoring(false);
            displayMessage(t("common.apiMsgs.notImplemented"), "error");
          }
        } else {
          cancelCallBack && cancelCallBack();
        }
      });
    },
    [t, displayMessage]
  );

  return { isRestoring, handleDeleteRestoreData };
};

export default useDataDeletionRestoration;
